/**
 * 标准距离配合目标偏移解析。
 *
 * 文件职责：
 * 1. 将 Distance Mate 的中心到中心、最小距离、最大距离条件转换为求解器可消费的目标偏移；
 * 2. 保持该逻辑独立于控制器和 UI，避免业务状态耦合求解器；
 * 3. 对无法折算的距离条件返回显式不可求解结果。
 */

import {
  AssemblyMateType,
  MateDistanceMode,
  type AssemblyMateDefinition,
  type MateEntityRef,
} from '../../definition';
import { resolveDefaultStandardMateDistanceMode } from './StandardMateMeasurement';
import {
  calculateStandardMateDistanceRadiusOffset,
  resolveStandardMateDistanceEntityModes,
} from './StandardMateRoundDistanceMode';

/** 距离目标计算容差。 */
const STANDARD_MATE_DISTANCE_TARGET_EPSILON = 1e-9;

/**
 * Distance Mate 目标偏移输入。
 */
export interface StandardMateDistanceTargetInput {
  /** 当前配合定义。 */
  readonly mate: AssemblyMateDefinition;
  /** 当前求解公式中的第一实体。 */
  readonly firstEntity: MateEntityRef;
  /** 当前求解公式中的第二实体。 */
  readonly secondEntity: MateEntityRef;
  /** 已按尺寸反转与移动侧修正后的有符号距离。 */
  readonly signedDistance: number;
}

/**
 * 按距离条件计算求解器目标偏移。
 *
 * @param input 目标偏移输入。
 * @returns 求解器要使用的有符号偏移；当前条件不可求解时返回 null。
 */
export function resolveStandardMateDistanceOffsetForSolver(
  input: StandardMateDistanceTargetInput,
): number | null {
  if (input.mate.mateType !== AssemblyMateType.Distance) {
    return input.signedDistance;
  }

  const distanceMode: MateDistanceMode = input.mate.dimension?.distanceMode
    ?? resolveDefaultStandardMateDistanceMode(input.firstEntity, input.secondEntity);

  if (distanceMode === MateDistanceMode.CenterToCenter && !input.mate.dimension?.distanceEntityModes) {
    return input.signedDistance;
  }

  const distanceEntityModes = resolveStandardMateDistanceEntityModes(
    input.firstEntity,
    input.secondEntity,
    distanceMode,
    input.mate.dimension?.distanceEntityModes,
  );
  const radiusOffset = calculateStandardMateDistanceRadiusOffset(
    input.firstEntity,
    input.secondEntity,
    distanceEntityModes,
  );
  if (Math.abs(radiusOffset) <= STANDARD_MATE_DISTANCE_TARGET_EPSILON) {
    return input.signedDistance;
  }

  const sign = input.signedDistance < 0 ? -1 : 1;
  const absoluteDistance = Math.abs(input.signedDistance);
  const absoluteCenterDistance = absoluteDistance + radiusOffset;

  /**
   * Maximum Distance 约束的是圆形实体远离另一实体的表面。
   * 当输入尺寸小于圆形实体半径时，中心线会跨到尺寸方向的另一侧；
   * 这在 SOLIDWORKS 中仍是可解状态，因此不能把负中心偏移截断或判空。
   */
  return Math.abs(absoluteCenterDistance) <= STANDARD_MATE_DISTANCE_TARGET_EPSILON
    ? 0
    : sign * absoluteCenterDistance;
}
